import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ObjectTypes, ApiErrorCodes, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import store from '../../../store';
import {PlanGetListSchema, PlanFeatureModes} from '../../../api/DBSchema/Plan';
import DescriptionCell from '../../../components/core/SMCTable/CommonCells/DescriptionCell';
import EditObjectCell from '../../../components/core/SMCTable/CommonCells/EditObjectCell';
import enabledLogo from '../../../assets/images/plan/enabled.png';
import disabledLogo from '../../../assets/images/plan/disabled.png';
import localLogo from '../../../assets/images/plan/local.png';
import {useSnackbar} from 'notistack';
import ToExcelSnackbarContent from '../../PopupPages/ToExcelSnackbarContent';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {buildStringSortCaseInsensitive} from '../../../utils/sortUtils';

const PlanFeatureModeCell = React.memo(({value}) => {
    const { t } = useTranslation();

    switch (value) {
        case PlanFeatureModes.enabled:
            return <img src={enabledLogo} title={t('SMC_GEN_ENABLED')} alt="enabled" />
        case PlanFeatureModes.disabled:
            return <img src={disabledLogo} title={t('SMC_GEN_DISABLED')} alt="disabled" />
        case PlanFeatureModes.locallyManaged:
            return <img src={localLogo} title={t('SMC_TEMPLATE_LOCALLY_MANAGED')} alt="local" />
        default:
            return null;
    }
});


export function usePlanResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueSnackbar } = useSnackbar();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('name', SortDirections.ASCENDING);

    const columns = useMemo(() => ([
        {id: 'name',                 accessor: PlanGetListSchema.name,                 header: t('SMP.NAME'),                                                          width: 135,                  defaultShow: true, sort: buildStringSortCaseInsensitive(PlanGetListSchema.name),        Cell: EditObjectCell,     },
        {id: 'description',          accessor: PlanGetListSchema.description,          header: '',                                         name: t('SMP.DESCRIPTION'), width: 25,  align: 'center', defaultShow: true, sort: buildStringSortCaseInsensitive(PlanGetListSchema.description), Cell: DescriptionCell     },
        {id: 'firewall',             accessor: PlanGetListSchema.firewall,             header: t('SMP.PLAN.TABLE.FIREWALL'),                                           width: 70,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'appsAndUrl',           accessor: PlanGetListSchema.appsAndUrl,           header: t('SMP.PLAN.TABLE.APPS_AND_URL'),                                       width: 105, align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'ips',                  accessor: PlanGetListSchema.ips,                  header: t('SMP.PLAN.TABLE.IPS'),                                                width: 35,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'traditionalAntiVirus', accessor: PlanGetListSchema.traditionalAntiVirus, header: t('SMP.PLANS.INNER_MENU.T_ANTI_VIRUS'),                                 width: 150, align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'antiSpam',             accessor: PlanGetListSchema.antiSpam,             header: t('SMP.PLAN.TABLE.ANTI_SPAM'),                                          width: 95,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'qos',                  accessor: PlanGetListSchema.qos,                  header: t('SMP.PLAN.TABLE.QoS'),                                                width: 45,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'remoteAccess',         accessor: PlanGetListSchema.remoteAccess,         header: t('SMP.PLAN.TABLE.REMOTE_ACCESS'),                                      width: 125, align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'siteToSite',           accessor: PlanGetListSchema.siteToSite,           header: t('SMP.PLAN.TABLE.SITE_TO_SITE'),                                       width: 90,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'userAwareness',        accessor: PlanGetListSchema.userAwareness,        header: t('SMP.PLAN.TABLE.USER_AWARENESS'),                                     width: 125, align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'antiVirus',            accessor: PlanGetListSchema.antiVirus,            header: t('SMP.PLAN.TABLE.ANTI_VIRUS'),                                         width: 80,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'antiBot',              accessor: PlanGetListSchema.antiBot,              header: t('SMP.PLAN.TABLE.ANTI_BOT'),                                           width: 80,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'threatEmulation',      accessor: PlanGetListSchema.threatEmulation,      header: t('SMP.PLANS.INNER_MENU.THREAT_EMULATION'),                             width: 130, align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'ssl',                  accessor: PlanGetListSchema.ssl,                  header: t('SMP.PLAN.TABLE.SSL'),                                                width: 45,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'logging',              accessor: PlanGetListSchema.logging,              header: t('SMP.PLAN.TABLE.LOGGING'),                                            width: 70,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'reports',              accessor: PlanGetListSchema.reports,              header: t('SMP.PLAN.TABLE.REPORTS'),                                            width: 73,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'firmwareUpgrades',     accessor: PlanGetListSchema.firmwareUpgrades,     header: t('SMP.PLAN.TABLE.FIRMWARE_UPGRADES'),                                  width: 140, align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'backup',               accessor: PlanGetListSchema.backup,               header: t('SMP.PLAN.TABLE.BACKUP'),                                             width: 65,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'dynamicDns',           accessor: PlanGetListSchema.dynamicDns,           header: t('SMP.PLAN.TABLE.DYNAMIC_DNS'),                                        width: 105, align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'notifications',        accessor: PlanGetListSchema.notifications,        header: t('SMP.PLAN.TABLE.NOTIFICATIONS'),                                      width: 96,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
        {id: 'enabled',              accessor: PlanGetListSchema.enabled,              header: t('SMP.ENABLED'),                                                       width: 70,  align: 'center', defaultShow: true,                                                                      Cell: PlanFeatureModeCell },
    ]), [t]);

    const rowIdSelector = obj => obj[PlanGetListSchema.name];

    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.PLAN, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.PLAN, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects,
        filterObject,
        setFilterObject,
        freeTextSearch
    } = useGenericResultsDataApi(ObjectTypes.PLAN);

    const newObjectCallback = useCallback((objectId) => {
        if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
        }
    }, []);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
            setIsPageLoaded(false);
            deleteSelectedObjects()
                    .then(({successMessage, errorMessage}) => {
                        successMessage && enqueueSuccessSnackbar(successMessage);
                        errorMessage && enqueueErrorSnackbar(errorMessage);
                        reloadTable();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoaded(true);
                    });
        }
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    const toExcelAction = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        enqueueSnackbar(null, {
            persist: true,
            content: (key) => (
                <ToExcelSnackbarContent
                    list={selectedObjects}
                    objectType="PLAN"
                    id={key}
                />
            )
        })
    }, [selectedObjects, enqueueSnackbar, t]);

    // Reload table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);

    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);
    
    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        newObjectCallback,
        reloadButtonOnClick,
        deleteButtonOnClick,
        toExcelAction
    });
}